import { Capacitor } from '@capacitor/core';
import type { Preferences as PreferencesPlugin } from '@capacitor/preferences';

/**
 * Persistent storage, deliberately behind an interface.
 *
 * Warning: The backend also delivers the refresh token through an httpOnly cookie,
 * and **that does not survive the Capacitor WebView**: the origin there is
 * `capacitor://localhost` (iOS) or `https://localhost` (Android) while the API
 * lives on another domain, so the cookie is third-party and dropped.
 *
 * On this client the refresh token is therefore an explicit value that **we**
 * store — hence this interface, whose implementation changes with the platform:
 *
 * - **web** → `localStorage`;
 * - **native** → `@capacitor/preferences`, which is `SharedPreferences` on
 *   Android and `UserDefaults` on iOS.
 *
 * ⚠️ The WebView has a `localStorage` too, and it would appear to work. It is
 * cleared with the WebView's data — by the system reclaiming space, by
 * "clear cache" in the app settings — and the reader is signed out with no
 * explanation. Native storage belongs to the application, not to its browser.
 *
 * @see docs/features/auth.md
 */
export interface Storage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/**
 * Namespaces every key, so nothing can collide with a third party's.
 *
 * @param name The bare key name.
 * @returns The namespaced key.
 */
export const storageKey = (name: string) => `meeply.${name}`;

const webStorage: Storage = {
  get: (key) => Promise.resolve(safely(() => localStorage.getItem(key), null)),
  set: (key, value) => {
    safely(() => localStorage.setItem(key, value), undefined);
    return Promise.resolve();
  },
  remove: (key) => {
    safely(() => localStorage.removeItem(key), undefined);
    return Promise.resolve();
  },
};

/**
 * The plugin **module**, fetched on first use and kept.
 *
 * Warning: imported dynamically, and that is the point. A static import puts
 * the plugin in the initial chunk of the **web** build, which never calls it —
 * on a budget that sits at 98 % with four kilobytes to spare, a dependency
 * loaded by nobody is not free. Here it is a separate chunk, requested once,
 * by native only.
 *
 * ⚠️ **The module, never the plugin object.** A Capacitor plugin is a `Proxy`
 * that answers *any* property as if it were a native method — `then` included.
 * Resolving a promise with it makes the runtime probe for `then` to decide
 * whether it is thenable, and that probe becomes a call to a native method
 * that does not exist:
 *
 *     Uncaught (in promise) "Preferences.then()" is not implemented on android
 *
 * It is thrown before the first render, so nothing mounts and the screen stays
 * blank with no other symptom. The web build never reaches this line, which is
 * why it survived the whole test suite until the application was first run on
 * a phone. The rule is one line long: never let a plugin be what a promise
 * resolves with.
 */
type PreferencesModule = { Preferences: typeof PreferencesPlugin };

let loading: Promise<PreferencesModule> | null = null;

function plugin(): Promise<PreferencesModule> {
  loading ??= import('@capacitor/preferences');
  return loading;
}

const nativeStorage: Storage = {
  get: async (key) => (await (await plugin()).Preferences.get({ key })).value,
  set: async (key, value) => {
    await (await plugin()).Preferences.set({ key, value });
  },
  remove: async (key) => {
    await (await plugin()).Preferences.remove({ key });
  },
};

/** `localStorage` throws in private browsing on some browsers. */
function safely<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/**
 * Warning: decided once, at load, from the platform Capacitor reports. Not per
 * call — a reader must never be served by one store and written to the other,
 * which is how a session survives a reload on one screen and not the next.
 */
export const storage: Storage = Capacitor.isNativePlatform()
  ? nativeStorage
  : webStorage;
